import { useState } from 'react';
import { cn } from '@mlm/ui/lib/cn';
import { CoachButton, Eyebrow } from '../primitives';
import { SouthIndianChart } from '../south-indian-chart';
import type { ChartResponse } from '../../lib/api';
import { chartMoonLine, chartTitle, instantDay } from '../../lib/format';

const degrees = (longitude: number) => {
  const inSign = longitude % 30;
  return `${Math.floor(inSign)}°${String(Math.floor((inSign % 1) * 60)).padStart(2, '0')}′`;
};

function Reading({ eyebrow, text }: { eyebrow: string; text: string }) {
  const [open, setOpen] = useState(false);
  const paragraphs = text.split(/\n\n+/);
  const shown = open ? paragraphs : paragraphs.slice(0, 1);
  return (
    <section className="border-b border-border px-5 py-6">
      <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
      <div className="space-y-3 text-[0.93rem] leading-relaxed text-ink/80">
        {shown.map((p, i) => (
          <p key={i} className={!open && paragraphs.length === 1 ? 'line-clamp-6' : undefined}>
            {p}
          </p>
        ))}
      </div>
      {(paragraphs.length > 1 || text.length > 520) && (
        <CoachButton kind="quiet" className="mt-3" onClick={() => setOpen(!open)}>
          {open ? 'Show less' : 'Read more'}
        </CoachButton>
      )}
    </section>
  );
}

/**
 * The chart grid, current period, planets and readings. `heading` renders the
 * title block itself (desktop Align hub).
 *
 * Split in two so the full-page chart can put the figures beside the prose on a
 * wide screen; the Align hub still composes both as one column.
 */
export function ChartPanel({ data, heading }: { data: ChartResponse; heading?: boolean }) {
  return (
    <>
      <ChartFigures data={data} heading={heading} />
      <ChartReadings data={data} />
    </>
  );
}

/** The chart square, warnings, current period and the planets table. */
export function ChartFigures({ data, heading }: { data: ChartResponse; heading?: boolean }) {
  const { chart: c, currentDasha: d } = data;
  return (
    <>
      {heading && (
        <div className="px-5 pb-6 pt-6 lg:pt-8">
          <Eyebrow className="mb-2.5">Your birth chart</Eyebrow>
          <h2 className="font-display text-[1.75rem] font-semibold leading-[1.15] text-ink xl:text-[2rem]">
            {chartTitle(data)}
          </h2>
          <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
            {chartMoonLine(data)}
          </p>
        </div>
      )}
      <div className={cn('border-b border-border px-5 pb-6', !heading && 'pt-6')}>
        <SouthIndianChart planets={c.planets} ascendant={c.ascendant?.sign ?? null} />
      </div>
      {c.warnings.length > 0 && (
        <div className="space-y-1 border-b border-border px-5 py-4 text-xs leading-relaxed text-muted-foreground">
          {c.warnings.map((w) => (
            <p key={w}>{w}</p>
          ))}
        </div>
      )}
      {d && (
        <section className="border-b border-border px-5 py-6">
          <Eyebrow className="mb-4">Current period</Eyebrow>
          <dl className="grid grid-cols-3 gap-4">
            {(
              [
                ['Mahadasha', d.maha],
                ['Antardasha', d.antar],
                ['Pratyantar', d.pratyantar],
              ] as const
            ).map(([label, span]) => (
              <div key={label}>
                <dt className="eyebrow">{label}</dt>
                <dd className="mt-1 font-display text-xl text-ink">{span.lord}</dd>
                <dd className="mt-0.5 text-xs text-muted-foreground">
                  until {instantDay(span.end)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}
      <section className="overflow-x-auto border-b border-border px-5 py-6">
        <Eyebrow className="mb-3">Planets</Eyebrow>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="eyebrow">
              <th className="py-2 font-medium">Planet</th>
              <th className="py-2 font-medium">Sign</th>
              <th className="py-2 font-medium">Degree</th>
              <th className="py-2 font-medium">House</th>
            </tr>
          </thead>
          <tbody>
            {c.planets.map((p) => (
              <tr key={p.name} className="border-t border-border text-ink">
                <td className="py-2">
                  {p.name}
                  {p.retrograde && p.name !== 'Rahu' && p.name !== 'Ketu' ? ' (R)' : ''}
                </td>
                <td className="py-2">{p.sign}</td>
                <td className="py-2 font-mono">{degrees(p.longitude)}</td>
                <td className="py-2 font-mono">{p.house || '·'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

/** The written readings and the provider note. */
export function ChartReadings({ data }: { data: ChartResponse }) {
  const { chart: c, readings, disclaimer } = data;
  return (
    <>
      {readings.lagna && <Reading eyebrow="Your ascendant" text={readings.lagna} />}
      <Reading eyebrow="Your Moon nakshatra" text={readings.nakshatra} />
      {readings.dasha && <Reading eyebrow="Your current period" text={readings.dasha} />}
      {readings.secondHouse && <Reading eyebrow="2nd house · wealth" text={readings.secondHouse} />}
      {readings.eleventhHouse && (
        <Reading eyebrow="11th house · gains" text={readings.eleventhHouse} />
      )}
      {readings.yogas
        .filter((y) => y.present)
        .map((y) => (
          <Reading key={y.name} eyebrow={y.name} text={y.explanation} />
        ))}
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {c.provider === 'prokerala'
          ? 'Calculated with Prokerala.'
          : 'Calculated with the built-in engine.'}{' '}
        {disclaimer}
      </p>
    </>
  );
}
